import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import type { DateTimePickerProps, TimeInputProps } from './DatePicker.types'
import { applyTimeMask, parseISO } from './DatePicker.utils'
import DatePickerField, { LABELS } from './DatePickerField'

const TIME_FMT = 'HH:mm:ss'

function formatTime(d: Date): string {
  return moment(d).format(TIME_FMT)
}

function parseTime(value: string): { hours: number; minutes: number; seconds: number } | null {
  const m = moment(value, ['HH:mm:ss', 'HH:mm'], true)
  if (!m.isValid()) return null
  return { hours: m.hours(), minutes: m.minutes(), seconds: m.seconds() }
}

export default function DateTimePickerField({
  name,
  dateLabel,
  timeLabel,
  required,
  disabled,
  floating,
  size = 'medium',
  error,
  clearable,
  initialValue,
  onChange,
}: DateTimePickerProps) {
  const initial = parseISO(initialValue ?? '')

  const [dateISO, setDateISO] = useState(() => {
    if (!initial) return ''
    const yyyy = initial.getFullYear()
    const mm = String(initial.getMonth() + 1).padStart(2, '0')
    const dd = String(initial.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  })

  const [timeText, setTimeText] = useState(() =>
    initial ? formatTime(initial) : '',
  )

  useEffect(() => {
    const parsed = parseISO(initialValue ?? '')
    if (!parsed) {
      setDateISO('')
      setTimeText('')
      return
    }
    const yyyy = parsed.getFullYear()
    const mm = String(parsed.getMonth() + 1).padStart(2, '0')
    const dd = String(parsed.getDate()).padStart(2, '0')
    setDateISO(`${yyyy}-${mm}-${dd}`)
    setTimeText(formatTime(parsed))
  }, [initialValue])

  const buildISO = useCallback((date: string, time: string): string => {
    if (!date) return ''
    const parsed = parseTime(time)
    if (time && !parsed) return ''
    if (!parsed) return `${date}T00:00:00`
    const hh = String(parsed.hours).padStart(2, '0')
    const mm = String(parsed.minutes).padStart(2, '0')
    const ss = String(parsed.seconds).padStart(2, '0')
    return `${date}T${hh}:${mm}:${ss}`
  }, [])

  const handleDateChange = useCallback((iso: string) => {
    setDateISO(iso)
    onChange?.(buildISO(iso, timeText))
  }, [timeText, onChange, buildISO])

  const handleTimeChange = useCallback((raw: string) => {
    setTimeText(raw)
    if (!raw || parseTime(raw)) {
      onChange?.(buildISO(dateISO, raw))
    }
  }, [dateISO, onChange, buildISO])

  const combinedValue = buildISO(dateISO, timeText)

  return (
    <div className={`rd-datetimepicker rd-datetimepicker--${size}`}>
      <DatePickerField
        name=""
        label={dateLabel ?? LABELS.dateLabel}
        required={required}
        disabled={disabled}
        floating={floating}
        size={size}
        error={error}
        clearable={clearable}
        initialValue={dateISO}
        onChange={handleDateChange}
      />

      <TimeInput
        value={timeText}
        label={timeLabel ?? LABELS.timeLabel}
        required={required}
        disabled={disabled}
        floating={floating}
        size={size}
        onChange={handleTimeChange}
      />

      <input type="hidden" name={name} value={combinedValue} />
    </div>
  )
}

function TimeInput({
  value,
  label,
  placeholder,
  required,
  disabled,
  floating,
  size = 'medium',
  error,
  onChange,
}: TimeInputProps) {
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const timeMask = LABELS.timeMask

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isDelete = (e.nativeEvent as InputEvent).inputType?.startsWith('delete') ?? false
      onChange(applyTimeMask(e.target.value, isDelete))
    },
    [onChange],
  )

  const handleBlur = useCallback(() => {
    setIsFocused(false)
    if (!value || parseTime(value)) return
    // pad partial: "12" → "12:00:00", "12:34" → "12:34:00"
    const digits = value.replace(/\D/g, '')
    if (!digits) return
    const padded = digits.padEnd(6, '0').slice(0, 6)
    onChange(`${padded.slice(0, 2)}:${padded.slice(2, 4)}:${padded.slice(4, 6)}`)
  }, [value, onChange])

  const hasValue = value.length > 0

  const wrapperCls = [
    'rd-timeinput',
    `rd-timeinput--${size}`,
    floating && 'rd-timeinput--floating',
    isFocused && 'rd-timeinput--focus',
    error && 'rd-timeinput--error',
    disabled && 'rd-timeinput--disabled',
    hasValue && 'has-value',
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperCls}>
      {label && !floating && (
        <label className={`rd-timeinput__label${required ? ' rd-timeinput__label--required' : ''}`}>
          {label}
        </label>
      )}

      <div className="rd-timeinput__wrapper">
        <input
          ref={inputRef}
          type="text"
          className="rd-timeinput__field"
          value={value}
          placeholder={floating ? '' : (placeholder ?? LABELS.timePlaceholder)}
          disabled={disabled}
          autoComplete="off"
          maxLength={timeMask.length}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
        />

        {floating && (
          <label className={`rd-timeinput__floating-label${required ? ' rd-timeinput__floating-label--required' : ''}`}>
            {label}
          </label>
        )}

        <span className="rd-timeinput__icon">
          <span className="rd-icon-clock" />
        </span>
      </div>
    </div>
  )
}
